/**
 * API + option/helper module for the Evaluation > Performance tab.
 *
 * Data is fetched from `/api/performance-metrics` endpoints (backed by the
 * `performance_metrics` and `machine_costs` collections seeded from
 * `performance_profiling.xlsx`).
 *
 * Document schema (see seed script + collections):
 *   performance_metrics: {
 *     base_model_name,
 *     concurrency_level (int: 1|10|100|1000|10000),
 *     length_category ("short"|"long"),
 *     cloud_provider (AWS|GCP|Azure), hardware_device (GPU|CPU),
 *     accelerator (T4|A10G|L40S|H100|L4|A100|null),
 *     instance_type, latency_ms: {min,median,avg,p90,p95,max}
 *   }
 *   machine_costs: {
 *     cloud_provider, hardware_device, accelerator, instance_type,
 *     on_demand_cost_per_hour_usd, spot_cost_per_hour_usd
 *   }
 */

import { data_from_server_post, data_from_server_get } from '@/shared/http/client';
import {
  CLOUD_PROVIDER,
  SELECTABLE_CLOUD_PROVIDERS,
  getDefaultEnvironment,
} from '../components/Performance/data/cloudProviders';

export { CLOUD_PROVIDER } from '../components/Performance/data/cloudProviders';

/**
 * @typedef {(1|10|100|1000|10000)} ConcurrencyLevel
 * @typedef {('short'|'long')} LengthCategory
 * @typedef {('student'|'teacher')} ModelKind
 * @typedef {('hourly'|'daily'|'monthly')} CostPeriod
 * @typedef {('on_demand'|'spot')} InstanceLifecycle
 *
 * @typedef {Object} EnvironmentSelection
 * @property {string} cloudProvider     e.g. "AWS"
 * @property {string} hardwareDevice    "GPU" | "CPU"
 * @property {string|null} accelerator  e.g. "T4" (null for CPU)
 * @property {string} instanceType      e.g. "g4dn.xlarge"
 *
 * @typedef {Object} PerformanceFilters
 * @property {ConcurrencyLevel} concurrency
 * @property {LengthCategory} lengthCategory
 * @property {EnvironmentSelection[]} environments
 *
 * @typedef {Object} CostConfig
 * @property {CostPeriod} period
 * @property {InstanceLifecycle} lifecycle
 *
 * @typedef {Object} StatRow
 * @property {string} key
 * @property {string} metric          // display label e.g. "Min", "P95"
 * @property {number|null} comparison // comparison-model value (null until merged client-side)
 * @property {number|null} current    // current-model value
 * @property {string} unit            // "ms"
 *
 * @typedef {Object} PerformanceMetricsPayload
 * @property {boolean} found
 * @property {string} currentModelLabel
 * @property {string|null} instanceType
 * @property {string|null} hardwareDevice
 * @property {string|null} accelerator
 * @property {string|null} cloudProvider
 * @property {number|null} concurrencyLevel
 * @property {string|null} lengthCategory
 * @property {number|null} costValue
 * @property {string|null} costUnit
 * @property {StatRow[]} inferenceLatency
 *
 * @typedef {Object} InstanceTypeOption
 * @property {string} value
 * @property {string} label
 *
 * @typedef {Object} PerformanceMetricsOptions
 * @property {number[]} concurrencyLevels
 * @property {LengthCategory[]} lengthCategories
 * @property {Object<string, Object<string, Object<string, InstanceTypeOption[]>>>} machineCatalog
 */

// -----------------------------------------------------------------------------
// Options shared by the filter modal / pills / seed script
// -----------------------------------------------------------------------------

export const CONCURRENCY_OPTIONS = [
  { value: 1, label: 'Single Stream (1 concurrent request)' },
  { value: 10, label: 'Parallel (10 concurrent requests)' },
  { value: 100, label: 'High Throughput (100 concurrent requests)' },
  { value: 1000, label: 'Massive Throughput (1,000 concurrent requests)' },
  { value: 10000, label: 'Extreme Scale (10,000 concurrent requests)' },
];

export const LENGTH_CATEGORY_OPTIONS = [
  { value: 'short', label: 'Short (<512 Tokens)' },
  { value: 'long', label: 'Long (>512 Tokens)' },
];

/**
 * Short, pill-friendly label per length category. The full
 * `LENGTH_CATEGORY_OPTIONS.label` includes the token-range hint which is
 * useful in the dropdown but too long for the filter-pill row, so the pill
 * uses these terse labels instead.
 */
const LENGTH_CATEGORY_PILL_LABEL = Object.freeze({
  short: 'Short',
  long: 'Long',
});

/**
 * Representative input-token count per length category. Used by
 * `InfrastructureComparisonTable` for the cost-per-1M-tokens math now
 * that the user picks a length bucket instead of typing a token count.
 */
export const LENGTH_TO_TOKENS = Object.freeze({
  short: 200,
  long: 5000,
});

/** Token-count threshold between short and long performance buckets (see seed script). */
export const LENGTH_CATEGORY_TIKTOKEN_THRESHOLD = 512;

/**
 * Mean per-record tiktoken count across benchmark `datasets_info` rows that
 * match `benchmarkNames` (or all benchmarks when names are omitted).
 *
 * The per-row average is derived from
 * `request_example_summary_data.num_tiktoken.sum / request_example_summary_data.num_records`
 * rather than the stored `num_tiktoken.avg`, which can be stale (it is only
 * recomputed on some write paths).
 *
 * @param {Array<Object>|null|undefined} datasetsInfo
 * @param {string[]|null|undefined} benchmarkNames
 * @returns {number|null}
 */
export function resolveBenchmarkAvgTiktoken(datasetsInfo, benchmarkNames) {
  if (!Array.isArray(datasetsInfo)) return null;
  const names =
    Array.isArray(benchmarkNames) && benchmarkNames.length
      ? new Set(benchmarkNames)
      : null;

  const avgs = datasetsInfo
    .filter((doc) => {
      if (!doc?.is_benchmark) return false;
      if (names && !names.has(doc.dataset)) return false;
      return true;
    })
    .map((doc) => {
      const summary = doc?.request_example_summary_data;
      const sum = summary?.num_tiktoken?.sum;
      const numRecords = summary?.num_records;
      if (
        typeof sum !== 'number' ||
        typeof numRecords !== 'number' ||
        numRecords <= 0
      ) {
        return null;
      }
      return sum / numRecords;
    })
    .filter((v) => typeof v === 'number' && Number.isFinite(v));

  if (!avgs.length) return null;
  return avgs.reduce((sum, v) => sum + v, 0) / avgs.length;
}

/**
 * @param {number|null|undefined} avgTiktoken
 * @returns {LengthCategory|null}
 */
export function getRecommendedLengthCategory(avgTiktoken) {
  if (typeof avgTiktoken !== 'number' || !Number.isFinite(avgTiktoken)) return null;
  if (avgTiktoken > LENGTH_CATEGORY_TIKTOKEN_THRESHOLD) return 'long';
  if (avgTiktoken < LENGTH_CATEGORY_TIKTOKEN_THRESHOLD) return 'short';
  return null;
}

/**
 * @param {Object} params
 * @param {string} params.clientId
 * @param {string} params.llmUsecaseId
 * @param {string[]|null|undefined} [params.benchmarkNames]
 * @returns {Promise<number|null>}
 */
export function fetchBenchmarkAvgTiktoken({ clientId, llmUsecaseId, benchmarkNames }) {
  if (!clientId || !llmUsecaseId) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    data_from_server_post(
      'datasets-and-benchmarks-meta-data',
      { client_id: clientId, llm_usecase_id: llmUsecaseId },
      (result) => {
        resolve(resolveBenchmarkAvgTiktoken(result?.datasets_info, benchmarkNames));
      },
      () => resolve(null),
    );
  });
}

export const CLOUD_PROVIDER_OPTIONS = SELECTABLE_CLOUD_PROVIDERS;

export const HARDWARE_DEVICE_OPTIONS = [
  { value: 'GPU', label: 'GPU' },
  { value: 'CPU', label: 'CPU' },
];

export const COST_PERIOD_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'monthly', label: 'Monthly' },
];

export const INSTANCE_PURCHASING_OPTIONS = [
  { value: 'on_demand', label: 'On Demand' },
  { value: 'spot', label: 'Spot' },
];

/**
 * Static fallback machine catalog used only until the live catalog
 * arrives from `GET /performance-metrics/options` (e.g. while the
 * Performance Filters modal is still loading the first time, or on
 * an offline fallback).
 *
 *   { [cloud]: { [hardware]: { [accelerator|'null']: InstanceTypeOption[] } } }
 *
 * The `'null'` accelerator key is used for CPU-only machines.
 *
 * The single source of truth at runtime is the DB-derived catalog —
 * see `fetchPerformanceMetricsOptions`.
 */
export const STATIC_FALLBACK_CATALOG = {
  AWS: {
    GPU: {
      T4: [{ value: 'g4dn.xlarge', label: 'g4dn.xlarge' }],
    },
  },
  GCP: {
    GPU: {
      T4: [{ value: 'n1-standard-8', label: 'n1-standard-8' }],
    },
  },
  Azure: {
    GPU: {
      T4: [{ value: 'Standard_NC4as_T4_v3', label: 'Standard_NC4as_T4_v3' }],
    },
  },
};

/**
 * Backwards-compatible alias kept for any external consumer that still
 * imports `CLOUD_MACHINE_CATALOG`. New code should prefer the live
 * catalog returned by `fetchPerformanceMetricsOptions`.
 */
export const CLOUD_MACHINE_CATALOG = STATIC_FALLBACK_CATALOG;

/** Return the accelerator options for the given cloud/hardware selection in `catalog`. */
export function getAcceleratorOptions(cloudProvider, hardwareDevice, catalog = STATIC_FALLBACK_CATALOG) {
  const perHardware = catalog?.[cloudProvider]?.[hardwareDevice];
  if (!perHardware) return [];
  return Object.keys(perHardware).map((acc) => ({
    value: acc === 'null' ? null : acc,
    label: acc === 'null' ? '(none)' : acc,
  }));
}

/** Return the instance-type options for the given cloud/hardware/accelerator selection in `catalog`. */
export function getInstanceTypeOptions(cloudProvider, hardwareDevice, accelerator, catalog = STATIC_FALLBACK_CATALOG) {
  const acceleratorKey = accelerator == null ? 'null' : accelerator;
  const perAccelerator =
    catalog?.[cloudProvider]?.[hardwareDevice]?.[acceleratorKey];
  return perAccelerator || [];
}

/** @type {PerformanceFilters} */
export const DEFAULT_PERF_FILTERS = {
  concurrency: 1,
  lengthCategory: 'short',
  environments: [getDefaultEnvironment()],
};

/** @type {CostConfig} */
export const DEFAULT_COST_CONFIG = {
  period: 'hourly',
  lifecycle: 'on_demand',
};

// -----------------------------------------------------------------------------
// API calls
// -----------------------------------------------------------------------------

/**
 * Fetch performance metrics for a single filter combination (single-view mode).
 * Uses the first environment in `filters.environments`.
 *
 * For `modelKind === 'teacher'`, the backend ignores the cloud / instance
 * fields and skips the cost lookup — teacher models have no
 * user-controlled runtime.
 *
 * @param {Object} params
 * @param {string} params.baseModelName
 * @param {PerformanceFilters} params.filters
 * @param {CostConfig} [params.costConfig]
 * @param {string} [params.currentModelLabel]
 * @param {ModelKind} [params.modelKind]
 * @returns {Promise<PerformanceMetricsPayload>}
 */
export async function fetchPerformanceMetrics({
  baseModelName,
  filters,
  costConfig = DEFAULT_COST_CONFIG,
  currentModelLabel = 'Specific AI V1',
  modelKind = 'student',
}) {
  const env = filters?.environments?.[0] || getDefaultEnvironment();
  return await data_from_server_post('performance-metrics', {
    base_model_name: baseModelName,
    concurrency_level: filters?.concurrency ?? 1,
    length_category: filters?.lengthCategory || 'short',
    model_kind: modelKind,
    cloud_provider: env.cloudProvider || CLOUD_PROVIDER.AWS,
    instance_type: env.instanceType,
    cost_period: costConfig?.period || 'hourly',
    cost_lifecycle: costConfig?.lifecycle || 'on_demand',
    current_model_label: currentModelLabel,
  });
}

/**
 * Fetch the Infrastructure Comparison rows — one row per environment the
 * user configured in the Performance Filters modal.
 *
 * When `comparisonModelKind === 'teacher'` and a comparison base model is
 * supplied, the backend appends an extra row for the teacher's latency at
 * the same (concurrency × length). Teacher rows have no infra and no cost
 * — only latency — so the cost columns render as "-".
 *
 * @param {Object} params
 * @param {string} params.baseModelName
 * @param {PerformanceFilters} params.filters
 * @param {CostConfig} [params.costConfig]
 * @param {string} [params.currentModelLabel]
 * @param {string|null} [params.comparisonBaseModelName]
 * @param {ModelKind} [params.comparisonModelKind]
 * @param {string|null} [params.comparisonModelLabel]
 * @returns {Promise<Array<Object>>}
 */
export async function fetchInfrastructureComparison({
  baseModelName,
  filters,
  costConfig = DEFAULT_COST_CONFIG,
  currentModelLabel = 'Specific AI V1',
  comparisonBaseModelName = null,
  comparisonModelKind = 'student',
  comparisonModelLabel = null,
}) {
  return await data_from_server_post(
    'performance-metrics/infrastructure-comparison',
    {
      base_model_name: baseModelName,
      concurrency_level: filters?.concurrency ?? 1,
      length_category: filters?.lengthCategory || 'short',
      environments: filters?.environments || [],
      cost_period: costConfig?.period || 'monthly',
      cost_lifecycle: costConfig?.lifecycle || 'on_demand',
      current_model_label: currentModelLabel,
      comparison_base_model_name: comparisonBaseModelName,
      comparison_model_kind: comparisonModelKind,
      comparison_model_label: comparisonModelLabel,
    }
  );
}

/**
 * Fetch the catalog of seeded options for a given base model. Powers the
 * Performance Filters modal — the user can only pick combinations that
 * actually exist in the `performance_metrics` collection.
 *
 * @param {Object} params
 * @param {string} params.baseModelName
 * @returns {Promise<PerformanceMetricsOptions>}
 */
export async function fetchPerformanceMetricsOptions({ baseModelName }) {
  if (!baseModelName) {
    return { concurrencyLevels: [], lengthCategories: [], machineCatalog: {} };
  }
  return await new Promise((resolve, reject) => {
    const suffix = `performance-metrics/options?base_model_name=${encodeURIComponent(baseModelName)}`;
    data_from_server_get(suffix, (data) => {
      if (data && typeof data === 'object') resolve(data);
      else reject(new Error('Invalid response from /performance-metrics/options'));
    });
  });
}

// -----------------------------------------------------------------------------
// Pure helpers used by the filter row + badge
// -----------------------------------------------------------------------------

/**
 * Build the human-readable filter pills shown above the metrics tables.
 *
 * @param {PerformanceFilters} filters
 * @returns {{key: string, label: string}[]}
 */
export function summarizeFiltersAsPills(filters) {
  const pills = [];
  const env = filters?.environments?.[0];
  if (env?.cloudProvider) pills.push({ key: 'cloud', label: env.cloudProvider });
  if (env?.hardwareDevice) pills.push({ key: 'hw', label: env.hardwareDevice });
  if (env?.accelerator) pills.push({ key: 'acc', label: `Accelerator: ${env.accelerator}` });
  if (env?.instanceType) pills.push({ key: 'instance', label: env.instanceType });
  if (filters?.concurrency != null) {
    const opt = CONCURRENCY_OPTIONS.find((o) => o.value === filters.concurrency);
    pills.push({
      key: 'concurrency',
      label: opt?.label || `${filters.concurrency} concurrency`,
    });
  }
  if (filters?.lengthCategory) {
    const pillLabel =
      LENGTH_CATEGORY_PILL_LABEL[filters.lengthCategory] || filters.lengthCategory;
    pills.push({
      key: 'length',
      label: `${pillLabel} input length`,
    });
  }
  return pills;
}

/**
 * Counts how many filter dimensions are active. Used for the "Filter Metrics" badge.
 * @param {PerformanceFilters} filters
 * @returns {number}
 */
export function countActiveFilters(filters) {
  return summarizeFiltersAsPills(filters).length;
}
